import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Award, Calendar } from 'lucide-react';

const PeakPerformance = ({ userId, className = '' }) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchPeakPerformance();
    }
  }, [userId]);

  const fetchPeakPerformance = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/peak-performance`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch peak performance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            שיא ביצועים
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.max_win_streak === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            שיא ביצועים
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500 py-4">
            <TrendingUp className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>עדיין אין רצף ניצחונות</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5" />
          שיא ביצועים
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* רצף ניצחונות */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="w-6 h-6 text-orange-600" />
              <span className="text-sm text-gray-700 font-medium">רצף ניצחונות מקסימלי</span>
            </div>
            <div className="text-5xl font-bold text-orange-600 mb-1">
              {data.max_win_streak}
            </div>
            <div className="text-sm text-gray-600">ניצחונות רצופים</div>
          </div>

          {/* תקופת השיא */}
          {data.peak_period && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-700 bg-purple-50 p-3 rounded-lg">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">תקופת השיא:</span>
              <Badge variant="outline" className="text-purple-700 border-purple-300">
                {data.peak_period}
              </Badge>
            </div>
          )}

          {/* פירוט משחקי השיא */}
          {data.peak_matches && data.peak_matches.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2 text-gray-700">
                משחקים בתקופת השיא:
              </h4>
              <div className="space-y-2">
                {data.peak_matches.map((match, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200 text-sm"
                  >
                    <Badge className="bg-green-600 text-white text-xs">
                      {idx + 1}
                    </Badge>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        ניצחון מול {match.opponent}
                      </p>
                      <p className="text-xs text-gray-600">{match.tournament}</p>
                    </div>
                  </div>
                ))}
                {data.max_win_streak > data.peak_matches.length && (
                  <p className="text-xs text-gray-500 text-center">
                    ועוד {data.max_win_streak - data.peak_matches.length} ניצחונות נוספים...
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PeakPerformance;
